/**
 * CampaignArchivePage — "Campaign Archive" page (FS-93).
 *
 * UX mirrors standard archive convention:
 *   - Topbar          title + primary "Restore selected" / "Purge selected"
 *   - Filter bar      text search, date-archived (from/to), reason substring
 *   - Archive table   name, archived-at, reason, clicks, conversions, row actions
 *   - Row actions     Restore (POST /unarchive/:id) + Purge (confirm then POST)
 *
 * All wire traffic goes through `./api` (x-tenant-id: t1). No raw fetch
 * lives in this file per the D6 dash-contract rule.
 *
 * Palette: green-only per task brief. Primary: green-600 / green-700.
 * Surfaces: white + zinc-200 + zinc-900. No emoji, no dark mode, no
 * competitor imagery.
 */
import { useMemo, useState, type ChangeEvent } from "react";
import {
	useCampaignArchiveList,
	useCampaignArchivePurge,
	useCampaignArchiveRestore,
} from "./hooks";
import type { ArchivedCampaign } from "./api";

// ---------------------------------------------------------------------------
// Design tokens — inline so JSX stays scan-able.
// ---------------------------------------------------------------------------

const SHELL = "mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8";
const CARD = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const CARD_PAD = `${CARD} p-5`;
const LABEL = "block text-xs font-medium uppercase tracking-wider text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const PRIMARY_BTN =
	"inline-flex items-center gap-2 rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50";
const GHOST_BTN =
	"inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50";
const DANGER_BTN =
	"inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50";
const TABLE_HEAD =
	"border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500";
const TABLE_CELL = "border-b border-zinc-100 px-4 py-3 text-sm text-zinc-800";

// ---------------------------------------------------------------------------
// Inline SVGs — no emoji per brief.
// ---------------------------------------------------------------------------

function IconRestore({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M3 7v6h6" />
			<path d="M3 13a9 9 0 1 0 3-6.7L3 9" />
		</svg>
	);
}

function IconTrash({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M3 6h18" />
			<path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
			<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
			<path d="M10 11v6" />
			<path d="M14 11v6" />
		</svg>
	);
}

function IconSearch({ className = "h-4 w-4" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconArchive({ className = "h-5 w-5" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<rect x="3" y="4" width="18" height="5" rx="1" />
			<path d="M5 9v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" />
			<path d="M10 13h4" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Format helpers — pure, exported for tests if ever needed.
// ---------------------------------------------------------------------------

function formatArchivedAt(ms: number): string {
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return "-";
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	const h = String(d.getUTCHours()).padStart(2, "0");
	const min = String(d.getUTCMinutes()).padStart(2, "0");
	return `${y}-${m}-${day} ${h}:${min}`;
}

function ymd(ms: number): string {
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return "";
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function ymdToEpoch(s: string, endOfDay: boolean): number | null {
	// Accepts YYYY-MM-DD. Returns UTC ms. `endOfDay` bumps to 23:59:59.999.
	if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
	const [y, m, d] = s.split("-").map((v) => Number(v));
	if (!y || !m || !d) return null;
	const base = Date.UTC(y, m - 1, d);
	return endOfDay ? base + 86399_999 : base;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignArchivePage() {
	const listQuery = useCampaignArchiveList();
	const restoreMut = useCampaignArchiveRestore();
	const purgeMut = useCampaignArchivePurge();

	const [search, setSearch] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [reasonFilter, setReasonFilter] = useState("");
	const [pendingPurgeId, setPendingPurgeId] = useState<string | null>(null);

	const rows: ArchivedCampaign[] = listQuery.data?.archived ?? [];

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		const rq = reasonFilter.trim().toLowerCase();
		const from = dateFrom ? ymdToEpoch(dateFrom, false) : null;
		const to = dateTo ? ymdToEpoch(dateTo, true) : null;
		return rows.filter((r) => {
			if (q) {
				const hay = `${r.name ?? ""} ${r.campaign_id}`.toLowerCase();
				if (!hay.includes(q)) return false;
			}
			if (rq) {
				if (!r.reason || !r.reason.toLowerCase().includes(rq)) return false;
			}
			if (from !== null && r.archived_at < from) return false;
			if (to !== null && r.archived_at > to) return false;
			return true;
		});
	}, [rows, search, dateFrom, dateTo, reasonFilter]);

	const handleRestore = (id: string) => {
		if (restoreMut.isPending) return;
		restoreMut.mutate(id);
	};

	const handlePurgeRequest = (id: string) => {
		setPendingPurgeId(id);
	};

	const handlePurgeConfirm = () => {
		if (!pendingPurgeId || purgeMut.isPending) return;
		const id = pendingPurgeId;
		purgeMut.mutate(id, {
			onSettled: () => setPendingPurgeId(null),
		});
	};

	const handlePurgeCancel = () => {
		setPendingPurgeId(null);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) =>
		setSearch(e.currentTarget.value);
	const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) =>
		setReasonFilter(e.currentTarget.value);
	const handleFromChange = (e: ChangeEvent<HTMLInputElement>) =>
		setDateFrom(e.currentTarget.value);
	const handleToChange = (e: ChangeEvent<HTMLInputElement>) =>
		setDateTo(e.currentTarget.value);
	const handleClearFilters = () => {
		setSearch("");
		setDateFrom("");
		setDateTo("");
		setReasonFilter("");
	};

	const isLoading = listQuery.isLoading;
	const errorMsg = listQuery.error?.message;
	const mutationError = restoreMut.error?.message ?? purgeMut.error?.message ?? null;

	return (
		<main
			data-testid="campaign-archive-page"
			className={SHELL}
		>
			{/* Topbar */}
			<header
				className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
				data-testid="campaign-archive-topbar"
			>
				<div className="flex items-start gap-3">
					<span
						className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-green-600/20 bg-green-50 text-green-700"
						aria-hidden="true"
					>
						<IconArchive />
					</span>
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Campaigns
						</p>
						<h1 className="text-2xl font-semibold text-zinc-900">
							Campaign Archive
						</h1>
						<p className="mt-1 text-sm text-zinc-500">
							{listQuery.isFetching
								? "Loading archived campaigns."
								: `${filtered.length} of ${rows.length} archived campaign${rows.length === 1 ? "" : "s"}`}
						</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						className={GHOST_BTN}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="campaign-archive-refresh"
					>
						Refresh
					</button>
				</div>
			</header>

			{/* Filter bar */}
			<section
				className={CARD_PAD}
				aria-label="Archive filters"
				data-testid="campaign-archive-filters"
			>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<div className="flex flex-col gap-1">
						<label
							htmlFor="campaign-archive-search"
							className={LABEL}
						>
							Search
						</label>
						<div className="relative">
							<span
								className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-zinc-400"
								aria-hidden="true"
							>
								<IconSearch />
							</span>
							<input
								id="campaign-archive-search"
								type="search"
								className={`${INPUT} pl-9`}
								placeholder="Name or ID"
								value={search}
								onChange={handleSearchChange}
								data-testid="campaign-archive-search-input"
							/>
						</div>
					</div>
					<div className="flex flex-col gap-1">
						<label
							htmlFor="campaign-archive-from"
							className={LABEL}
						>
							Archived from
						</label>
						<input
							id="campaign-archive-from"
							type="date"
							className={INPUT}
							value={dateFrom}
							onChange={handleFromChange}
							data-testid="campaign-archive-date-from"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label
							htmlFor="campaign-archive-to"
							className={LABEL}
						>
							Archived to
						</label>
						<input
							id="campaign-archive-to"
							type="date"
							className={INPUT}
							value={dateTo}
							onChange={handleToChange}
							data-testid="campaign-archive-date-to"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label
							htmlFor="campaign-archive-reason"
							className={LABEL}
						>
							Reason contains
						</label>
						<input
							id="campaign-archive-reason"
							type="text"
							className={INPUT}
							placeholder="e.g. ended, merged"
							value={reasonFilter}
							onChange={handleReasonChange}
							data-testid="campaign-archive-reason-filter"
						/>
					</div>
				</div>
				<div className="mt-4 flex items-center justify-end gap-2">
					<button
						type="button"
						className={GHOST_BTN}
						onClick={handleClearFilters}
						data-testid="campaign-archive-clear-filters"
					>
						Clear filters
					</button>
				</div>
			</section>

			{/* Mutation feedback banner */}
			{mutationError ? (
				<div
					role="alert"
					className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
					data-testid="campaign-archive-mutation-error"
				>
					{mutationError}
				</div>
			) : null}
			{restoreMut.isSuccess && !restoreMut.isPending ? (
				<div
					role="status"
					className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
					data-testid="campaign-archive-restore-success"
				>
					Campaign restored. It is now active again.
				</div>
			) : null}
			{purgeMut.isSuccess && !purgeMut.isPending ? (
				<div
					role="status"
					className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
					data-testid="campaign-archive-purge-success"
				>
					Archive marker removed.
				</div>
			) : null}

			{/* Table */}
			<section
				className={CARD}
				aria-label="Archived campaigns"
				data-testid="campaign-archive-table-section"
			>
				{isLoading ? (
					<div
						className="px-4 py-10 text-center text-sm text-zinc-500"
						data-testid="campaign-archive-loading"
					>
						Loading archived campaigns.
					</div>
				) : errorMsg ? (
					<div
						role="alert"
						className="px-4 py-10 text-center text-sm text-red-700"
						data-testid="campaign-archive-error"
					>
						{errorMsg}
					</div>
				) : filtered.length === 0 ? (
					<div
						className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-zinc-500"
						data-testid="campaign-archive-empty"
					>
						<span className="text-green-700" aria-hidden="true">
							<IconArchive />
						</span>
						<p className="font-medium text-zinc-700">
							{rows.length === 0
								? "No archived campaigns yet."
								: "No campaigns match the current filters."}
						</p>
						<p className="text-xs text-zinc-500">
							{rows.length === 0
								? "Archived campaigns will appear here."
								: "Adjust the filters or clear them to see more."}
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-left"
							data-testid="campaign-archive-table"
						>
							<thead>
								<tr>
									<th scope="col" className={TABLE_HEAD}>
										Campaign
									</th>
									<th scope="col" className={TABLE_HEAD}>
										Archived at
									</th>
									<th scope="col" className={TABLE_HEAD}>
										Reason
									</th>
									<th scope="col" className={`${TABLE_HEAD} text-right`}>
										Clicks
									</th>
									<th scope="col" className={`${TABLE_HEAD} text-right`}>
										Conversions
									</th>
									<th scope="col" className={`${TABLE_HEAD} text-right`}>
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((row) => (
									<tr
										key={row.campaign_id}
										data-testid={`campaign-archive-row-${row.campaign_id}`}
									>
										<td className={TABLE_CELL}>
											<div className="flex flex-col">
												<span className="font-medium text-zinc-900">
													{row.name && row.name.length > 0
														? row.name
														: row.campaign_id}
												</span>
												<span className="font-mono text-xs text-zinc-500">
													{row.campaign_id}
												</span>
											</div>
										</td>
										<td className={TABLE_CELL}>
											<div className="flex flex-col">
												<span className="text-zinc-800">
													{formatArchivedAt(row.archived_at)}
												</span>
												<span className="text-xs text-zinc-500">
													by {row.archived_by}
												</span>
											</div>
										</td>
										<td className={TABLE_CELL}>
											{row.reason ? (
												<span>{row.reason}</span>
											) : (
												<span className="text-zinc-400">-</span>
											)}
										</td>
										<td className={`${TABLE_CELL} text-right font-mono`}>
											{typeof row.clicks === "number"
												? row.clicks.toLocaleString()
												: "-"}
										</td>
										<td className={`${TABLE_CELL} text-right font-mono`}>
											{typeof row.conversions === "number"
												? row.conversions.toLocaleString()
												: "-"}
										</td>
										<td className={`${TABLE_CELL} text-right`}>
											<div className="inline-flex items-center gap-2">
												<button
													type="button"
													className={PRIMARY_BTN}
													onClick={() => handleRestore(row.campaign_id)}
													disabled={
														restoreMut.isPending &&
														restoreMut.variables === row.campaign_id
													}
													data-testid={`campaign-archive-restore-${row.campaign_id}`}
													aria-label={`Restore ${row.campaign_id}`}
												>
													<IconRestore />
													Restore
												</button>
												<button
													type="button"
													className={DANGER_BTN}
													onClick={() => handlePurgeRequest(row.campaign_id)}
													disabled={
														purgeMut.isPending &&
														purgeMut.variables === row.campaign_id
													}
													data-testid={`campaign-archive-purge-${row.campaign_id}`}
													aria-label={`Purge ${row.campaign_id}`}
												>
													<IconTrash />
													Purge
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{/* Footer / meta line */}
				<div className="flex items-center justify-between border-t border-zinc-100 px-4 py-3 text-xs text-zinc-500">
					<span data-testid="campaign-archive-count">
						Showing {filtered.length} archived row
						{filtered.length === 1 ? "" : "s"}
					</span>
					{rows.length > 0 ? (
						<span>Tenant: t1</span>
					) : null}
				</div>
			</section>

			{/* Purge confirm modal — lightweight, no portal dep. */}
			{pendingPurgeId ? (
				<div
					className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 p-4"
					role="dialog"
					aria-modal="true"
					aria-labelledby="campaign-archive-purge-title"
					data-testid="campaign-archive-purge-confirm"
				>
					<div className="w-full max-w-md rounded-[8px] border border-zinc-200 bg-white shadow-lg">
						<div className="border-b border-zinc-100 px-5 py-4">
							<h2
								id="campaign-archive-purge-title"
								className="text-base font-semibold text-zinc-900"
							>
								Purge archived campaign?
							</h2>
						</div>
						<div className="space-y-2 px-5 py-4 text-sm text-zinc-700">
							<p>
								This will permanently remove the archive marker for{" "}
								<span className="font-mono text-xs text-zinc-900">
									{pendingPurgeId}
								</span>
								.
							</p>
							<p className="text-xs text-zinc-500">
								Historical reports referencing this campaign id remain
								readable.
							</p>
						</div>
						<div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-5 py-3">
							<button
								type="button"
								className={GHOST_BTN}
								onClick={handlePurgeCancel}
								disabled={purgeMut.isPending}
								data-testid="campaign-archive-purge-cancel"
							>
								Cancel
							</button>
							<button
								type="button"
								className={PRIMARY_BTN}
								onClick={handlePurgeConfirm}
								disabled={purgeMut.isPending}
								data-testid="campaign-archive-purge-confirm-btn"
							>
								<IconTrash />
								{purgeMut.isPending ? "Purging." : "Confirm purge"}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</main>
	);
}

// Export helper for convenience — not part of the public dash contract but
// useful if another feature ever mounts archive date formatting.
export { formatArchivedAt, ymd };

export default CampaignArchivePage;
